<script lang="ts">
    // popup ancorado de uso interno do Zgrid2 (menu de ações, colunas, filtro, "⋯" da linha).
    // position: fixed calculado a partir do elemento âncora — assim não é cortado pelo overflow do card do grid.
    // fica dentro do DOM do .zgrid2 (sem portal) de propósito, pra herdar as variáveis de cor do tema.
    import { createEventDispatcher, onDestroy, onMount } from 'svelte'

    export let anchor: HTMLElement
    export let align: 'left' | 'right' = 'left'
    export let width: number = 280

    const dispatch = createEventDispatcher()
    const GAP = 6
    const MARGIN = 8

    let popup: HTMLElement
    let top = 0
    let left = 0
    let placed = false
    let observer: ResizeObserver = null

    function place() {
        if (!anchor || !popup) return
        const rect = anchor.getBoundingClientRect()
        const popupWidth = popup.offsetWidth
        const popupHeight = popup.offsetHeight
        let x = align === 'right' ? rect.right - popupWidth : rect.left
        x = Math.max(MARGIN, Math.min(x, window.innerWidth - popupWidth - MARGIN))
        let y = rect.bottom + GAP
        if (y + popupHeight > window.innerHeight - MARGIN) {
            const above = rect.top - GAP - popupHeight
            y = above >= MARGIN ? above : Math.max(MARGIN, window.innerHeight - popupHeight - MARGIN)
        }
        top = y
        left = x
        placed = true
    }

    function close() {
        dispatch('close')
    }

    function onKeydown(event: KeyboardEvent) {
        if (event.key === 'Escape') close()
    }

    // ao rolar a página o popup acompanha a âncora, e só fecha quando ela sai da tela. fechar em qualquer rolagem
    // derruba o popup no celular, onde o próprio toque costuma gerar uma rolagem. rolar a lista dentro dele não conta
    function onScroll(event: Event) {
        if (popup && popup.contains(event.target as Node)) return
        const rect = anchor?.getBoundingClientRect()
        if (!rect || rect.bottom < 0 || rect.top > window.innerHeight) {
            close()
            return
        }
        place()
    }

    onMount(() => {
        place()
        observer = new ResizeObserver(() => place())
        observer.observe(popup)
        window.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', place)
    })

    onDestroy(() => {
        observer?.disconnect()
        window.removeEventListener('scroll', onScroll, true)
        window.removeEventListener('resize', place)
    })
</script>

<svelte:window on:keydown={onKeydown} />

<div class="z2pop_backdrop" role="presentation" on:click={close}></div>
<div class="z2pop" class:is-placed={placed} bind:this={popup} style="top:{top}px;left:{left}px;width:{width}px;">
    <slot />
</div>

<style>
    .z2pop_backdrop {
        position: fixed;
        inset: 0;
        z-index: 1060;
        background: transparent;
    }
    .z2pop {
        position: fixed;
        z-index: 1061;
        max-width: calc(100vw - 16px);
        max-height: calc(100vh - 16px);
        display: flex;
        flex-direction: column;
        /* reservas para quando é usado fora do Zgrid2 (dropdown do date-select): lá não existem as --z2-*,
           então caem nos tokens globais de campo, que já seguem o tema */
        background: var(--z2-popup, var(--z-field-solid-bg));
        color: var(--z2-text, var(--z-field-text));
        border: 1px solid var(--z2-border, var(--z-field-border));
        border-radius: 10px;
        box-shadow: var(--z2-shadow, 0 12px 32px rgba(0, 0, 0, 0.35));
        overflow: hidden;
        visibility: hidden;
        font-size: 14px;
    }
    .z2pop.is-placed {
        visibility: visible;
    }
</style>
